import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'components/ui/button';
import { useTheme } from 'components/core/theme-provider';
import { useTranslation } from 'react-i18next';
import { getFile } from 'services/storage-service';
import { useConfig } from 'hooks/use-config';
import PDFViewer from './pdf-viewer';
import pdfIcon from 'assets/images/pdf-icon.svg';
import documentIcon from 'assets/images/document-icon.svg';

interface BaseFile {
  id: string | number;
  name: string;
  fileType: string;
}

interface BaseCardProps<T extends BaseFile> {
  file: T;
  onViewDetails?: (file: T) => void;
  renderActions: (file: T) => React.ReactNode;
  IconComponent: React.ComponentType<{ className?: string }>;
  iconColor: string;
  backgroundColor: string;
  doubleClick?: (url: string) => void;
}

interface BaseGridViewProps<T extends BaseFile> {
  onViewDetails?: (file: T) => void;
  filters: Record<string, any>;
  data?: {
    data: T[];
    totalCount: number;
  };
  isLoading: boolean;
  error?: any;
  onLoadMore: () => void;
  renderDetailsSheet: (file: T | null, isOpen: boolean, onClose: () => void) => React.ReactNode;
  getFileTypeIcon: (fileType: string) => React.ComponentType<{ className?: string }>;
  getFileTypeInfo: (fileType: string) => { iconColor: string; backgroundColor: string };
  renderActions: (file: T) => React.ReactNode;
  emptyStateConfig: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    description: string;
  };
  sectionLabels: {
    folder: string;
    file: string;
  };
  errorMessage: string;
  loadingMessage: string;
  loadMoreLabel: string;
  // Additional props for customization
  additionalFiles?: T[];
  filterFiles?: (files: T[], filters: Record<string, any>) => T[];
  processFiles?: (files: T[]) => T[];
  isDetailsOpen: boolean;
  selectedFile: T | null;
  handleCloseDetails: () => void;
}

// Common Card Component
const CommonCard = <T extends BaseFile>({
  file,
  onViewDetails,
  renderActions,
  IconComponent,
  iconColor,
  backgroundColor,
  doubleClick,
}: BaseCardProps<T>) => {
  const isFolder = file.fileType === 'Folder';
  const [fileUrl, setFileUrl] = useState<string | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);

  const isFetchingRef = useRef(false);

  useEffect(() => {
    async function getFileUrlAsync() {
      if (file.fileType !== 'Photo') return;
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        const url = await getFileUrl(file);
        setFileUrl(url);
      } catch (error) {
        console.error('Error fetching file URL:', error);
      } finally {
        isFetchingRef.current = false;
      }
    }
    getFileUrlAsync();
  }, [file.fileType, file.id, file.name]);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isActionButton = target.closest('[data-action-button="true"]');

    if (!isActionButton) {
      e.preventDefault();
      onViewDetails?.(file);
    }
  };

  const handleDoubleClick = async (e: React.MouseEvent) => {
    if (file.fileType !== 'Pdf' && file.fileType !== 'Photo') return;
    e.preventDefault();
    if (!fileUrl) {
      const url = await getFileUrl(file);
      if (url) {
        doubleClick?.(url);
      }
    } else {
      doubleClick?.(fileUrl);
    }
  };

  const theme = useTheme();
  const containerClasses =
    'group relative bg-card rounded-[8px] border border-gray hover:border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer h-[160px] overflow-hidden';
  // 'group relative bg-card rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer w-[213px] h-[160px]';
  const contentClasses = isFolder
    ? 'p-3 flex items-center space-x-3'
    : 'p-6 flex flex-col items-center text-center space-y-4';

  const iconContainerClasses = `${isFolder ? 'w-8 h-8' : 'w-20 h-20'} flex items-center justify-center ${isFolder ? backgroundColor : ''}`;
  // const iconClasses = `${isFolder ? 'w-5 h-5' : 'w-14 h-14'} ${iconColor}`;
  const renderFolderLayout = () => (
    <div className="flex items-center justify-between">
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-gray-900 truncate" title={file.name}>
          {file.name}
        </h3>
      </div>
      <section data-action-button="true" className="flex-shrink-0" aria-label="File actions">
        {renderActions(file)}
      </section>
    </div>
  );

  const renderFileLayout = () => (
    <div className="flex items-center justify-between space-x-2 mt-2 p-1 bg-white rounded-b-[8px]">
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center`}>
          <IconComponent className={`w-4 h-4 ${iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={theme.theme === 'dark' ? 'text-sm font-medium text-gray truncate' : 'text-sm font-medium text-gray-900 truncate'} title={file.name}>
            {file.name}
          </h3>
        </div>
      </div>
      <section data-action-button="true" className="flex-shrink-0" aria-label="File actions">
        {renderActions(file)}
      </section>
    </div>
  );

  const { configurationSettings } = useConfig();

  async function getFileUrl<T extends BaseFile>(file: T): Promise<string | undefined> {
    const response = await getFile(file.id.toString(), configurationSettings, undefined);
    return response?.url;
  }


  const filesImageViewer = () => {
    const fileIcon = file?.fileType !== 'Document' ? pdfIcon : documentIcon;
    return <div className="w-full h-full rounded-lg flex items-center justify-center">
      <img
        src={fileIcon}
        alt={file.name}
        className={`object-cover w-full h-full rounded-[8px] transition-opacity duration-300`}
      />
    </div>;
  };

  return (
    <button
      className={containerClasses}
      onClick={handleCardClick}
      aria-label={`View details for ${file.name}`}
      type="button"
      onDoubleClick={handleDoubleClick}
    >
      <div className="relative h-[160px]">
        {file.fileType === 'Photo' ? (
          <>
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center rounded">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" opacity="0.9" color='#dc3848' height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-image-icon lucide-image">
                  <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
                  <circle cx="9" cy="9" r="2" />
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                </svg>
              </div>
            )}

            {fileUrl && (
              <div className="h-full w-full rounded-[8px] overflow-hidden relative">
                <img
                  src={fileUrl}
                  alt={file.name}
                  onLoad={() => setLoaded(true)}
                  className={`object-cover w-full h-full rounded-[8px] transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                />
                <div className={`absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 light:bg-dark-25`}>
                  {renderFileLayout()}
                </div>
              </div>

            )}
          </>
        ) : (
          <div className={`${contentClasses} h-full relative flex justify-center items-center`}>
            <div className={iconContainerClasses}>
              {/* <IconComponent className={iconClasses} /> */}
              {filesImageViewer()}
            </div>
            <div className={`${isFolder ? 'flex-1' : 'w-full'} absolute bottom-0 left-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 light:bg-dark-25`}>
              {isFolder ? renderFolderLayout() : renderFileLayout()}
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

export const CommonGridView = <T extends BaseFile>({
  onViewDetails,
  filters,
  data,
  isLoading,
  error,
  onLoadMore,
  renderDetailsSheet,
  getFileTypeIcon,
  getFileTypeInfo,
  renderActions,
  emptyStateConfig,
  sectionLabels,
  errorMessage,
  loadingMessage,
  loadMoreLabel,
  isDetailsOpen,
  selectedFile,
  handleCloseDetails,
}: BaseGridViewProps<T>) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | undefined>(undefined);
  const hasActiveFilters = Object.values(filters).some(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
  );

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-red-600 mb-2">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (isLoading && !data?.data.length) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4 text-primary"></div>
          <p className="text-gray-500">{t(loadingMessage)}</p>
        </div>
      </div>
    );
  }
  console.log('sectionLabels', sectionLabels);

  return (
    <div className="flex h-full w-full">
      <div className={`flex flex-col h-full ${isDetailsOpen ? 'flex-1' : 'w-full'}`}>
        <div className="flex-1">
          <div className="space-y-8">
            <div className="flex-1 overflow-y-auto h-[calc(100vh-180px)]">
              <div className="space-y-8 pl-4 pr-4 pb-4">
                {data && data.data?.length > 0 && (
                  <div>
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
                      {data?.data.map((file) => {
                        const IconComponent = getFileTypeIcon(file.fileType);
                        const { iconColor, backgroundColor } = getFileTypeInfo(file.fileType);

                        return (
                          <CommonCard
                            key={`${file.id}-${file.name}`}
                            file={file}
                            onViewDetails={onViewDetails}
                            renderActions={renderActions}
                            IconComponent={IconComponent}
                            iconColor={iconColor}
                            backgroundColor={backgroundColor}
                            doubleClick={(url) => setSelectedFileUrl(url)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {data?.data.length === 0 && !isLoading && (
                  <div className="flex flex-col items-center justify-center p-12 text-center">
                    <emptyStateConfig.icon className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className={theme.theme === 'dark' ? 'text-lg font-medium text-gray mb-2' : 'text-lg font-medium text-gray-900 mb-2'}>{emptyStateConfig.title}</h3>
                    <p className={theme.theme === 'dark' ? 'text-gray max-w-sm' : 'text-gray-500 max-w-sm'}>
                      {hasActiveFilters
                        ? 'No files match the current criteria'
                        : emptyStateConfig.description}
                    </p>
                  </div>
                )}

                {data && data.data.length < data.totalCount && (
                  <div className="flex justify-center pt-6">
                    <Button
                      onClick={onLoadMore}
                      variant="outline"
                      disabled={isLoading}
                      className="min-w-32"
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                          {t(loadingMessage)}
                        </div>
                      ) : (
                        loadMoreLabel
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          {selectedFileUrl && (
            <PDFViewer url={selectedFileUrl} setUrl={setSelectedFileUrl} fileType={selectedFile?.fileType} />
          )}
        </div>
      </div>
      {isDetailsOpen && (
        <div className="w-100 flex-shrink-0 border-l h-full overflow-hidden">
          {renderDetailsSheet(selectedFile, isDetailsOpen, handleCloseDetails)}
        </div>
      )}
    </div>
  );
};
